'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { MapPin, Mountain, Waves, Send, CheckCircle, Search } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { parseAuthToken } from '@/utils/base64'
import { toast } from '@/components/ui/ToastContainer'
type SpotType = 'valley' | 'sea_spot'

const DIFFICULTIES = [
  { value: 'beginner', label: '초보' },
  { value: 'intermediate', label: '중급' },
  { value: 'advanced', label: '고급' },
  { value: 'expert', label: '전문가' },
]
const SPOT_TYPES = ['다이빙', '스노클링', '다이빙/스노클링', '기타']

interface ValleyForm {
  name: string
  address: string
  province: string
  city: string
  avg_depth: string
  max_depth: string
  family_score: string
  adult_score: string
  youtube_urls: string
  weather_info: string
}

interface SeaSpotForm {
  name: string
  address: string
  province: string
  city: string
  min_depth: string
  max_depth: string
  visibility: string
  difficulty: string
  spot_type: string
  features: string
  description: string
  youtube_urls: string
}

const EMPTY_VALLEY: ValleyForm = {
  name: '', address: '', province: '', city: '',
  avg_depth: '', max_depth: '',
  family_score: '50', adult_score: '50',
  youtube_urls: '', weather_info: '',
}

const EMPTY_SEA: SeaSpotForm = {
  name: '', address: '', province: '', city: '',
  min_depth: '', max_depth: '',
  visibility: '5', difficulty: 'beginner',
  spot_type: '다이빙', features: '', description: '', youtube_urls: '',
}

declare global {
  interface Window {
    daum: { Postcode: new (options: { oncomplete: (data: DaumPostcodeResult) => void }) => { open: () => void } }
  }
}

interface DaumPostcodeResult {
  roadAddress: string
  jibunAddress: string
  sido: string
  sigungu: string
}

export default function SpotSubmitPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null)
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<SpotType>('valley')
  const [valleyForm, setValleyForm] = useState<ValleyForm>(EMPTY_VALLEY)
  const [seaForm, setSeaForm] = useState<SeaSpotForm>(EMPTY_SEA)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [mySubmissions, setMySubmissions] = useState<{ id: string; type: string; name: string; status: string; admin_note?: string }[]>([])
  // 수정 모드
  const [editId, setEditId] = useState<string | null>(null)

  useEffect(() => {
    // ?edit=ID 파라미터 확인
    const params = new URLSearchParams(window.location.search)
    const id = params.get('edit')
    if (id) setEditId(id)

    checkAuth(id || undefined)
    // Daum 주소 스크립트 미리 로드
    if (!document.getElementById('daum-postcode-script')) {
      const s = document.createElement('script')
      s.id = 'daum-postcode-script'
      s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      document.head.appendChild(s)
    }
  }, [])

  const checkAuth = async (editIdParam?: string) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    if (!token) { router.replace('/login'); return }
    try {
      const decoded = parseAuthToken(token)
      const supabase = getSupabaseClient()
      let query = supabase.from('user_profiles').select('id')
      if (decoded.kakao_id) query = query.eq('kakao_id', decoded.kakao_id)
      else if (decoded.naver_id) query = query.eq('naver_id', decoded.naver_id)
      const { data } = await query.single()
      if (data) {
        setIsLoggedIn(true)
        setAuthToken(token)
        loadMySubmissions(token)
        // 수정 모드: 기존 제보 불러와서 폼 채우기
        if (editIdParam) {
          await loadSubmissionForEdit(editIdParam, token)
        }
      } else {
        router.replace('/login')
      }
    } catch {
      router.replace('/login')
    }
  }

  const loadSubmissionForEdit = async (id: string, token: string) => {
    const res = await fetch(`/api/spot-submissions?id=${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const json = await res.json()
    const s = json.data
    if (!s) return
    if (s.type === 'valley') {
      setActiveType('valley')
      setValleyForm({
        name: s.name || '',
        address: s.address || '',
        province: s.province || '',
        city: s.city || '',
        avg_depth: s.avg_depth != null ? String(s.avg_depth) : '',
        max_depth: s.max_depth != null ? String(s.max_depth) : '',
        family_score: s.family_score != null ? String(s.family_score) : '50',
        adult_score: s.adult_score != null ? String(s.adult_score) : '50',
        youtube_urls: s.youtube_urls || '',
        weather_info: s.weather_info || '',
      })
    } else if (s.type === 'sea_spot') {
      setActiveType('sea_spot')
      setSeaForm({
        name: s.name || '',
        address: s.address || '',
        province: s.province || '',
        city: s.city || '',
        min_depth: s.min_depth != null ? String(s.min_depth) : '',
        max_depth: s.max_depth != null ? String(s.max_depth) : '',
        visibility: s.visibility != null ? String(s.visibility) : '5',
        difficulty: s.difficulty || 'beginner',
        spot_type: s.spot_type || '다이빙',
        features: s.features || '',
        description: s.description || '',
        youtube_urls: s.youtube_urls || '',
      })
    }
  }

  const loadMySubmissions = async (token: string) => {
    const res = await fetch('/api/spot-submissions', {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.ok) {
      const json = await res.json()
      setMySubmissions(json.data || [])
    }
  }

  const openAddressSearch = () => {
    if (typeof window === 'undefined') return

    const doOpen = () => {
      new window.daum.Postcode({
        oncomplete: (data: DaumPostcodeResult) => {
          const address = data.roadAddress || data.jibunAddress
          const province = data.sido
          const city = data.sigungu

          if (activeType === 'valley') {
            setValleyForm(prev => ({ ...prev, address, province, city }))
          } else {
            setSeaForm(prev => ({ ...prev, address, province, city }))
          }
        }
      }).open()
    }

    if (window.daum?.Postcode) {
      doOpen()
    } else {
      // 스크립트 로드 대기
      const script = document.getElementById('daum-postcode-script')
      if (script) {
        script.addEventListener('load', doOpen, { once: true })
      } else {
        const s = document.createElement('script')
        s.id = 'daum-postcode-script'
        s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
        s.onload = doOpen
        document.head.appendChild(s)
      }
    }
  }

  const handleValleyChange = (field: keyof ValleyForm, value: string) => {
    setValleyForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSeaChange = (field: keyof SeaSpotForm, value: string) => {
    setSeaForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!authToken) return

    let body: Record<string, unknown>

    if (activeType === 'valley') {
      if (!valleyForm.name.trim() || !valleyForm.address.trim()) {
        toast.error('계곡명과 주소를 입력해주세요')
        return
      }
      body = {
        type: 'valley',
        name: valleyForm.name.trim(),
        address: valleyForm.address.trim(),
        province: valleyForm.province,
        city: valleyForm.city.trim(),
        latitude: 0,
        longitude: 0,
        avg_depth: parseFloat(valleyForm.avg_depth) || 0,
        max_depth: parseFloat(valleyForm.max_depth) || 0,
        family_score: parseInt(valleyForm.family_score) || 50,
        adult_score: parseInt(valleyForm.adult_score) || 50,
        rating: 4.0,
        youtube_urls: valleyForm.youtube_urls.trim(),
        weather_info: valleyForm.weather_info.trim(),
      }
    } else {
      if (!seaForm.name.trim() || !seaForm.address.trim()) {
        toast.error('포인트명과 주소를 입력해주세요')
        return
      }
      body = {
        type: 'sea_spot',
        name: seaForm.name.trim(),
        address: seaForm.address.trim(),
        province: seaForm.province,
        city: seaForm.city.trim(),
        latitude: 0,
        longitude: 0,
        min_depth: parseFloat(seaForm.min_depth) || 0,
        max_depth: parseFloat(seaForm.max_depth) || 0,
        visibility: parseInt(seaForm.visibility) || 5,
        difficulty: seaForm.difficulty,
        spot_type: seaForm.spot_type,
        features: seaForm.features.trim(),
        description: seaForm.description.trim(),
        youtube_urls: seaForm.youtube_urls.trim(),
      }
    }

    setIsSubmitting(true)
    const isEdit = !!editId
    const res = await fetch('/api/spot-submissions', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(isEdit ? { id: editId, ...body } : body),
    })

    if (!res.ok) {
      const json = await res.json().catch(() => ({}))
      toast.error(json.error || '제출 중 오류가 발생했습니다')
      setIsSubmitting(false)
      return
    }

    if (!isEdit) {
      if (activeType === 'valley') setValleyForm(EMPTY_VALLEY)
      else setSeaForm(EMPTY_SEA)
    }

    setIsSubmitting(false)
    setSubmitted(true)
    loadMySubmissions(authToken)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const inputCls = `w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`
  const labelCls = `block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`

  const statusBadge = (status: string) => {
    if (status === 'pending') return <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">검토중</span>
    if (status === 'approved') return <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">승인됨</span>
    return <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">반려</span>
  }

  // 인증 확인 중 (checkAuth에서 미로그인 시 router.replace('/login') 처리)
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* 헤더 */}
      <div className={`${isDark ? 'bg-gradient-to-r from-gray-800 to-gray-900' : 'bg-gradient-to-r from-cyan-50 to-blue-50'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/10'}`}>
              <MapPin className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editId ? '제보 수정 (재제출)' : '포인트 제보하기'}
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {editId ? '내용을 수정하고 다시 제출하면 재심사됩니다' : '새로운 계곡 또는 바다 포인트를 제보해주세요'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* 승인 완료 메시지 */}
        {submitted && (
          <div className={`rounded-2xl p-5 border ${isDark ? 'bg-green-900/20 border-green-700/40' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className={`font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                  {editId ? '수정 내용이 재제출되었습니다!' : '제보가 접수되었습니다!'}
                </p>
                <p className={`text-sm mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  관리자 검토 후 승인되면 알림으로 알려드립니다. 소중한 제보 감사합니다!
                </p>
                {!editId && (
                  <button onClick={() => setSubmitted(false)} className="mt-2 text-xs text-green-500 underline">
                    추가 제보하기
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!submitted && (
          <div className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            {/* 타입 선택 탭 */}
            <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setActiveType('valley')}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all ${
                  activeType === 'valley' ? 'bg-blue-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Mountain className="w-4 h-4" />
                계곡 제보
              </button>
              <button
                onClick={() => setActiveType('sea_spot')}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all ${
                  activeType === 'sea_spot' ? 'bg-blue-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Waves className="w-4 h-4" />
                바다 포인트 제보
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* 이름 */}
              <div>
                <label className={labelCls}>{activeType === 'valley' ? '계곡명' : '포인트명'} <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={activeType === 'valley' ? valleyForm.name : seaForm.name}
                  onChange={e => activeType === 'valley' ? handleValleyChange('name', e.target.value) : handleSeaChange('name', e.target.value)}
                  placeholder={activeType === 'valley' ? '예: 조무락골 계곡' : '예: 서귀포 문섬'}
                  className={inputCls}
                />
              </div>

              {/* 주소 - 클릭 시 검색 팝업 */}
              <div>
                <label className={labelCls}>주소 <span className="text-red-400">*</span></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={activeType === 'valley' ? valleyForm.address : seaForm.address}
                    onClick={openAddressSearch}
                    placeholder="클릭하여 주소 검색"
                    className={`${inputCls} cursor-pointer flex-1`}
                  />
                  <button
                    type="button"
                    onClick={openAddressSearch}
                    className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors flex-shrink-0"
                  >
                    <Search className="w-4 h-4" />
                    검색
                  </button>
                </div>
                {/* 자동 파싱된 도/시 표시 */}
                {(activeType === 'valley' ? valleyForm.province : seaForm.province) && (
                  <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📍 {activeType === 'valley' ? valleyForm.province : seaForm.province}
                    {(activeType === 'valley' ? valleyForm.city : seaForm.city) && ` · ${activeType === 'valley' ? valleyForm.city : seaForm.city}`}
                  </p>
                )}
              </div>

              {/* 계곡 전용 필드 */}
              {activeType === 'valley' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>평균 수심 (m)</label>
                      <input type="number" step="0.1" value={valleyForm.avg_depth} onChange={e => handleValleyChange('avg_depth', e.target.value)} placeholder="예: 1.5" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>최대 수심 (m)</label>
                      <input type="number" step="0.1" value={valleyForm.max_depth} onChange={e => handleValleyChange('max_depth', e.target.value)} placeholder="예: 3.0" className={inputCls} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>가족 추천도 (0~100)</label>
                      <input type="number" min={0} max={100} value={valleyForm.family_score} onChange={e => handleValleyChange('family_score', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>어른 추천도 (0~100)</label>
                      <input type="number" min={0} max={100} value={valleyForm.adult_score} onChange={e => handleValleyChange('adult_score', e.target.value)} className={inputCls} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>날씨/특이사항</label>
                    <textarea rows={2} value={valleyForm.weather_info} onChange={e => handleValleyChange('weather_info', e.target.value)} placeholder="수질, 주차, 편의시설 등 알려주세요" className={`${inputCls} resize-none`} />
                  </div>

                  <div>
                    <label className={labelCls}>유튜브 영상 URL (선택)</label>
                    <input type="url" value={valleyForm.youtube_urls} onChange={e => handleValleyChange('youtube_urls', e.target.value)} placeholder="https://youtube.com/..." className={inputCls} />
                  </div>
                </>
              )}

              {/* 바다 포인트 전용 필드 */}
              {activeType === 'sea_spot' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>최소 수심 (m)</label>
                      <input type="number" step="0.5" value={seaForm.min_depth} onChange={e => handleSeaChange('min_depth', e.target.value)} placeholder="예: 5" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>최대 수심 (m)</label>
                      <input type="number" step="0.5" value={seaForm.max_depth} onChange={e => handleSeaChange('max_depth', e.target.value)} placeholder="예: 25" className={inputCls} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>평균 시야 (m)</label>
                      <input type="number" min={1} value={seaForm.visibility} onChange={e => handleSeaChange('visibility', e.target.value)} placeholder="예: 10" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>난이도</label>
                      <select value={seaForm.difficulty} onChange={e => handleSeaChange('difficulty', e.target.value)} className={inputCls}>
                        {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>종류</label>
                    <div className="flex gap-2 flex-wrap">
                      {SPOT_TYPES.map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => handleSeaChange('spot_type', t)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            seaForm.spot_type === t ? 'bg-blue-500 text-white' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>특징 / 주요 볼거리</label>
                    <textarea rows={2} value={seaForm.features} onChange={e => handleSeaChange('features', e.target.value)} placeholder="예: 산호군락, 돌돔 등 다양한 어종" className={`${inputCls} resize-none`} />
                  </div>

                  <div>
                    <label className={labelCls}>상세 설명</label>
                    <textarea rows={3} value={seaForm.description} onChange={e => handleSeaChange('description', e.target.value)} placeholder="접근 방법, 주의사항, 시즌 등 자유롭게 작성해주세요" className={`${inputCls} resize-none`} />
                  </div>

                  <div>
                    <label className={labelCls}>유튜브 영상 URL (선택)</label>
                    <input type="url" value={seaForm.youtube_urls} onChange={e => handleSeaChange('youtube_urls', e.target.value)} placeholder="https://youtube.com/..." className={inputCls} />
                  </div>
                </>
              )}

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-3.5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 transition-all shadow-lg ${
                  activeType === 'valley'
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600'
                    : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                } disabled:opacity-60`}
              >
                {isSubmitting ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    제보 신청하기
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* 내 제보 현황 */}
        {mySubmissions.length > 0 && (
          <div className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>내 제보 현황</p>
            </div>
            <div className="divide-y">
              {mySubmissions.map(s => (
                <div key={s.id} className={`px-4 py-3 flex items-center gap-3 ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    s.type === 'valley' ? isDark ? 'bg-blue-500/20' : 'bg-blue-50' : isDark ? 'bg-blue-500/20' : 'bg-blue-50'
                  }`}>
                    {s.type === 'valley'
                      ? <Mountain className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      : <Waves className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.name}</p>
                    {s.admin_note && s.status === 'rejected' && (
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>사유: {s.admin_note}</p>
                    )}
                  </div>
                  {statusBadge(s.status)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
